/**
 * Structured Logger
 *
 * Provides structured logging with JSON output option,
 * correlation ID support, and consistent formatting.
 */

import { redactMessage, redactData } from './redact.js';

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';
export type LogFormat = 'text' | 'json';

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

const LOG_COLORS: Record<LogLevel, string> = {
  debug: '\x1b[90m', // Gray
  info: '\x1b[36m',  // Cyan
  warn: '\x1b[33m',  // Yellow
  error: '\x1b[31m', // Red
};

const RESET = '\x1b[0m';

/**
 * Log entry structure for JSON output
 */
export interface LogEntry {
  timestamp: string;
  level: LogLevel;
  context: string;
  message: string;
  correlationId?: string | undefined;
  data?: Record<string, unknown> | undefined;
  error?: {
    message: string;
    stack?: string | undefined;
  } | undefined;
}

/**
 * Logger configuration
 */
export interface LoggerConfig {
  level: LogLevel;
  format: LogFormat;
}

/**
 * Global logger configuration
 */
let globalConfig: LoggerConfig = {
  level: 'info',
  format: 'text',
};

/**
 * Configure global logger settings
 */
export function configureLogger(config: Partial<LoggerConfig>): void {
  globalConfig = { ...globalConfig, ...config };
}

/**
 * Entry handed to a registered log sink — a side channel for shipping logs to a
 * durable store (e.g. Supabase `log_events`) without changing any call site.
 */
export interface LogSinkEntry {
  level: LogLevel;
  context: string;
  message: string;
  correlationId?: string | undefined;
  data?: Record<string, unknown> | undefined;
  error?: { message: string; stack?: string | undefined } | undefined;
}

type LogSink = (entry: LogSinkEntry) => void;
let globalSink: LogSink | undefined;
let sinkMinLevel: LogLevel = 'info';

/**
 * Register a side-channel sink that receives every log at >= `minLevel`. The
 * server uses this to push logs to `log_events`. The sink MUST be non-throwing
 * and fast (enqueue + return) — it runs inline after console output, guarded so
 * it can never break logging. Pass `undefined` to detach.
 */
export function registerLogSink(sink: LogSink | undefined, minLevel: LogLevel = 'info'): void {
  globalSink = sink;
  sinkMinLevel = minLevel;
}

/**
 * Structured Logger class
 */
export class Logger {
  private context: string;
  private defaultCorrelationId?: string | undefined;

  constructor(context: string, correlationId?: string) {
    this.context = context;
    this.defaultCorrelationId = correlationId;
  }

  /**
   * Create a child logger with additional context
   */
  child(subContext: string, correlationId?: string): Logger {
    return new Logger(
      `${this.context}:${subContext}`,
      correlationId ?? this.defaultCorrelationId
    );
  }

  /**
   * Set correlation ID for this logger instance
   */
  withCorrelationId(correlationId: string): Logger {
    return new Logger(this.context, correlationId);
  }

  private shouldLog(level: LogLevel): boolean {
    return LOG_LEVELS[level] >= LOG_LEVELS[globalConfig.level];
  }

  private formatTextLog(
    level: LogLevel,
    message: string,
    correlationId?: string,
    data?: Record<string, unknown>
  ): string {
    const timestamp = new Date().toISOString();
    const color = LOG_COLORS[level];
    const levelStr = level.toUpperCase().padEnd(5);
    const corrId = correlationId ? ` [${correlationId}]` : '';

    let output = `${color}${timestamp} ${levelStr} [${this.context}]${corrId}${RESET} ${message}`;

    if (data && Object.keys(data).length > 0) {
      output += ` ${JSON.stringify(data)}`;
    }

    return output;
  }

  private formatJsonLog(
    level: LogLevel,
    message: string,
    correlationId?: string,
    data?: Record<string, unknown>,
    error?: { message: string; stack?: string | undefined }
  ): string {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      context: this.context,
      message,
      correlationId,
      data,
    };

    if (error) {
      entry.error = {
        message: error.message,
        stack: error.stack,
      };
    }

    return JSON.stringify(entry);
  }

  private log(
    level: LogLevel,
    message: string,
    data?: Record<string, unknown> | Error,
    correlationId?: string
  ): void {
    if (!this.shouldLog(level)) return;

    const corrId = correlationId ?? this.defaultCorrelationId;
    let logData: Record<string, unknown> | undefined;
    let error: Error | undefined;

    if (data instanceof Error) {
      error = data;
    } else {
      logData = data;
    }

    // Redaction (LOG_REDACTION_V1, default on): scrub the message, structured
    // data, and error strings ONCE so console output AND the durable sink both
    // see the scrubbed form. When disabled — or when nothing matches — these
    // return their input by reference, so everything below is byte-identical to
    // pre-redaction output.
    const safeMessage = redactMessage(message);
    const safeData = redactData(logData);
    const safeError = error
      ? {
          message: redactMessage(error.message),
          stack: error.stack ? redactMessage(error.stack) : undefined,
        }
      : undefined;

    const output =
      globalConfig.format === 'json'
        ? this.formatJsonLog(level, safeMessage, corrId, safeData, safeError)
        : this.formatTextLog(level, safeMessage, corrId, safeData);

    // Use appropriate console method
    switch (level) {
      case 'debug':
        console.debug(output);
        break;
      case 'info':
        console.info(output);
        break;
      case 'warn':
        console.warn(output);
        break;
      case 'error':
        console.error(output);
        if (error && globalConfig.format === 'text') {
          console.error(safeError?.stack);
        }
        break;
    }

    // Side-channel: ship to the registered durable sink (e.g. log_events). Never
    // allowed to throw — diagnostics must not break the thing they observe.
    if (globalSink && LOG_LEVELS[level] >= LOG_LEVELS[sinkMinLevel]) {
      try {
        globalSink({
          level,
          context: this.context,
          message: safeMessage,
          correlationId: corrId,
          data: safeData,
          error: safeError,
        });
      } catch {
        // a sink must never break logging
      }
    }
  }

  debug(message: string, data?: Record<string, unknown>): void {
    this.log('debug', message, data);
  }

  info(message: string, data?: Record<string, unknown>): void {
    this.log('info', message, data);
  }

  warn(message: string, data?: Record<string, unknown>): void {
    this.log('warn', message, data);
  }

  error(message: string, error?: Error | Record<string, unknown>): void {
    this.log('error', message, error);
  }

  /**
   * Log with explicit correlation ID
   */
  logWithCorrelation(
    level: LogLevel,
    message: string,
    correlationId: string,
    data?: Record<string, unknown>
  ): void {
    this.log(level, message, data, correlationId);
  }
}

/**
 * Create a new logger instance
 */
export function createLogger(context: string, correlationId?: string): Logger {
  return new Logger(context, correlationId);
}

/**
 * Root logger instance
 */
export const rootLogger = createLogger('Bash');
